import { useCache } from "@/hooks/useCache";
import { useControls, button, folder } from "leva";
import { generateShareableUrl, useSetFilter, resetFilter, useFilterTitle, useFilterAuthor } from "@/hooks/useFilter";
import { useState, useCallback, useRef, useEffect } from "react";
import NutritionFacts from "@/components/nutrition/NutritionFacts";
import { Controls } from "./Controls";
import { useSpices } from "@/hooks/useSpices";
import { useTransitionGuard } from "@/context/Transitions";
import { useUiStore } from "@/stores/useUiStore";
import { ShareModal } from "./ShareModal";
import type { UiProps } from "@/ui";
import { Sheet } from "@/layouts/Sheet";
import { View } from "@/layouts/views/View";
import { DesktopView } from "@/layouts/views/DesktopView";
import { DesktopViewToggleButton } from "@/layouts/views/DesktopViewToggleButton";
import { Header } from "@/components/nutrition";
import { Info } from "lucide-react";

export function PostShake({ani}: UiProps) {
  const { resetFilter: resetSpices } = useSetFilter();
  const { clearAll } = useSpices();
  const [blendTitle] = useFilterTitle();
  const [author] = useFilterAuthor();
  const { setPhase } = useUiStore();
  const { trigger } = useTransitionGuard();

  const [showInfo, setShowInfo] = useState(false);
  const [isNutritionOpen, setIsNutritionOpen] = useState(true);
  const [isShareOpen, setIsShareOpen] = useState(false);

  const handleReset = useCallback(() => {
    setPhase('reset');
    ani.settle.invalidate().play().then(() => {
      clearAll();
      resetSpices();
      resetFilter()
      setTimeout(() => setPhase('preshake'), 0) // nuqs updates are async, but react updates aren't and so we risk getting race conditions and update with stale spices if we update too soon. Defer phase change by one macrotask to let nuqs process the reset before PreShake mounts
    });
  }, [ani.settle, clearAll, resetSpices, setPhase]);

  useControls(
    "UI",
    {
      postShake: folder(
        {
          reset: button(() => trigger('reset', { onContinue: handleReset })),
          share: button(async () => { await navigator.clipboard.writeText(generateShareableUrl()); alert(`Link copied: ${generateShareableUrl()}`); }), //prettier-ignore
          "add to cart": button(() => alert("adding to cart")),
        },
        { collapsed: false }
      ),
    },
    [trigger, handleReset]
  );

  // run
  const viewRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (!viewRef.current) return
    const style = viewRef.current.style;
    style.opacity = "0";
    ani.shake.then(() => setTimeout(() => {     style.opacity = "1"}, 250))
  }, [viewRef])

  return (
    <>
    
      {isShareOpen && <ShareModal isOpen shareUrl={generateShareableUrl()} onClose={() => setIsShareOpen(false)} />}

      {/* Mobile View */}
      <View ref={viewRef} className="postshake">
        {/* Controls */}
        <Controls
          className="pointer-events-auto w-full"
          onReset={() => trigger('reset', { onContinue: handleReset })}
          onShare={() => setIsShareOpen(true)}
          onInfo={() => setShowInfo((v) => !v)}
          showInfo={showInfo}
        />

        {/* Nutrition on mobile in a sheet */}
        <Sheet
          className="lg:hidden"
          state={showInfo ? "info" : "closed"}
          onClose={() => setShowInfo(false)}>
          <div onClick={() => setIsNutritionOpen(false)} className="p-4">
            <Header title={blendTitle} subtitle={author} className="text-secondary" />
          </div>
          <NutritionFacts gapClass="gap-6" />
        </Sheet>
      </View>

      {/* Nutrition */}
      <DesktopViewToggleButton isOpen={isNutritionOpen} onClick={() => setIsNutritionOpen(true)}>
        <Info size={20} />
      </DesktopViewToggleButton>

      <DesktopView ref={viewRef} isOpen={isNutritionOpen} position="right" className="postshake bg-primary bg-main">

        <div onClick={() => setIsNutritionOpen(false)} className="p-4  text-main bg-secondary">
          <Header title={blendTitle} subtitle={author} className="text-main" />
        </div>

        <NutritionFacts gapClass="gap-6" />

        <Controls
          className="pointer-events-auto w-full  bg-main border-t"
          onReset={() => trigger('reset', { onContinue: handleReset })}
          onShare={() => setIsShareOpen(true)}
          onInfo={() => setShowInfo((v) => !v)}
          showInfo={showInfo}
        />
      </DesktopView>
    </>
  );
}
